/**
 * OwnerStatisticsRepository
 * 오너 통계 API 호출 레이어
 */
import { API_BASE_URL } from '../../../config/api';
import type { OverallStatistics } from '../models/OwnerStatistics';

/**
 * OwnerStatisticsRepository 클래스
 * 통계 관련 API 호출 담당
 */
export class OwnerStatisticsRepository {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * 인증 헤더 생성
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * 전체 통계 조회
   */
  async getOverviewStatistics(days: number): Promise<OverallStatistics> {
    const response = await fetch(`${this.baseUrl}/v1/owner/statistics/overview?days=${days}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        // 인증 오류 - 적절한 로그인 페이지로 리다이렉트
        localStorage.removeItem('token');
        const hostname = window.location.hostname;
        // localhost, 스테이징 서버에서는 /studyroom prefix 사용
        const loginPath = (hostname === 'localhost' || hostname === '192.168.0.38' || hostname === '180.70.238.90')
          ? '/studyroom/owner/login'
          : '/owner/login';
        window.location.href = loginPath;
        throw new Error('인증이 만료되었습니다.');
      }
      throw new Error(`통계 데이터 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 매출 통계만 조회
   */
  async getRevenueStatistics(days: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}/v1/owner/statistics/revenue?days=${days}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('매출 통계 조회 실패');
    }

    return response.json();
  }

  /**
   * 회원 통계만 조회
   */
  async getMemberStatistics(days: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}/v1/owner/statistics/members?days=${days}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('회원 통계 조회 실패');
    }

    return response.json();
  }
}

/**
 * OwnerStatisticsRepository 싱글톤 인스턴스
 */
export const ownerStatisticsRepository = new OwnerStatisticsRepository();
